import React from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchUsersByPhoneList,
  fetchNonUsersByPhoneList,
  searchAppUsers,
  getFavoriteUsers,
  addUserToFavorites,
  removeUserFromFavorites,
  getGuestContacts,
} from '@/api/contactsApi'
import {
  mockContacts,
  mockSearchContacts,
  mockFavoriteContacts,
} from '@/mock/contactsData'
import { useUserStore } from '@/store'
import type { PhoneContactsPayload, SearchUsersParams } from '@/types/contacts'

// Development mode flag - set to true to use mock data
const USE_MOCK_DATA = false // Using real API like React Native

// Query keys
export const CONTACTS_QUERY_KEYS = {
  usersByPhone: 'usersByPhone',
  nonUsersByPhone: 'nonUsersByPhone',
  searchUsers: 'searchUsers',
  favorites: 'favorites',
  guestContacts: 'guestContacts',
} as const

// Fetch users by phone list
export const useFetchUsersByPhoneList = (payload: PhoneContactsPayload) => {
  return useQuery({
    queryKey: [CONTACTS_QUERY_KEYS.usersByPhone, payload.phones],
    queryFn: () => fetchUsersByPhoneList(payload),
    enabled: payload.phones.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Fetch non-users by phone list
export const useFetchNonUsersByPhoneList = (payload: PhoneContactsPayload) => {
  return useQuery({
    queryKey: [CONTACTS_QUERY_KEYS.nonUsersByPhone, payload.phones],
    queryFn: () => fetchNonUsersByPhoneList(payload),
    enabled: payload.phones.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Search app users
export const useSearchAppUsers = (params: SearchUsersParams) => {
  return useQuery({
    queryKey: [CONTACTS_QUERY_KEYS.searchUsers, params],
    queryFn: USE_MOCK_DATA
      ? () =>
          Promise.resolve({
            users: mockSearchContacts(params.query),
            pagination: {
              page: 1,
              page_size: 20,
              total_pages: 1,
              total_items: mockSearchContacts(params.query).length,
              has_next: false,
            },
          })
      : () => searchAppUsers(params),
    enabled: !!params.query && params.query.length > 0, // Search for any non-empty query
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

// Get favorite users
export const useFavoriteUsers = () => {
  const userAuthenticated = useUserStore((state) => state.userAuthenticated)
  const userId = userAuthenticated?.user_id

  return useQuery({
    queryKey: [CONTACTS_QUERY_KEYS.favorites, userId],
    queryFn: async () => {
      if (USE_MOCK_DATA) {
        return mockFavoriteContacts
      }

      if (!userId) {
        return []
      }

      try {
        const response = await getFavoriteUsers(userId)
        return response.users || []
      } catch {
        return []
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes like React Native version
    enabled: !!userId, // Only run query when userId is available
  })
}

// Get guest contacts
export const useGuestContacts = () => {
  return useQuery({
    queryKey: [CONTACTS_QUERY_KEYS.guestContacts],
    queryFn: getGuestContacts,
    staleTime: 30 * 60 * 1000, // 30 minutes
  })
}

// Add user to favorites mutation
export const useAddToFavorites = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: addUserToFavorites,
    onSuccess: (_, userId) => {
      // Invalidate favorites query for all users
      queryClient.invalidateQueries({
        queryKey: [CONTACTS_QUERY_KEYS.favorites],
      })
      // Also invalidate search results
      queryClient.invalidateQueries({
        queryKey: [CONTACTS_QUERY_KEYS.searchUsers],
      })
    },
  })
}

// Remove user from favorites mutation
export const useRemoveFromFavorites = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: removeUserFromFavorites,
    onSuccess: (_, userId) => {
      // Invalidate favorites query for all users
      queryClient.invalidateQueries({
        queryKey: [CONTACTS_QUERY_KEYS.favorites],
      })
      // Also invalidate search results
      queryClient.invalidateQueries({
        queryKey: [CONTACTS_QUERY_KEYS.searchUsers],
      })
    },
  })
}

// Combined hook for loading users and device contacts (similar to React Native version)
export const useLoadUsersAndDeviceContacts = ({
  search = '',
}: {
  search?: string
}) => {
  // Add separate debounce for API calls to prevent blocking like in React Native
  const [debouncedApiSearch, setDebouncedApiSearch] = React.useState(search) // Initialize immediately with the passed value
  const [isInitialized, setIsInitialized] = React.useState(false)

  React.useEffect(() => {
    // On first initialization, if there is a search query, execute search immediately
    if (!isInitialized && search && search.length > 0) {
      setDebouncedApiSearch(search)
      setIsInitialized(true)
      return
    }

    // Normal debounce for subsequent changes
    const timeout = setTimeout(() => {
      setDebouncedApiSearch(search)
      setIsInitialized(true)
    }, 800) // Aggressive debounce for API like React Native

    return () => clearTimeout(timeout)
  }, [search, isInitialized])

  const searchUsersQuery = useSearchAppUsers({
    query: debouncedApiSearch,
    exclude_blocked: true,
    page_size: 20,
  })

  const guestContactsQuery = useGuestContacts()

  // Get search results - like React Native version
  const getSearchResults = () => {
    if (USE_MOCK_DATA) {
      if (search.length > 0) {
        return mockSearchContacts(search)
      }
      // Return non-favorite contacts when not searching
      return mockContacts.filter((contact) => !contact.is_favorite)
    }

    // Real API logic - only search when debounced query is provided
    if (debouncedApiSearch && debouncedApiSearch.length > 0) {
      return searchUsersQuery.data?.users || []
    }

    // When no search, return empty array (like React Native)
    // User contacts are loaded from device/phone list in React Native
    // In web/mini app we don't have device contacts
    return []
  }

  return {
    // Search results
    searchedUsers: getSearchResults(),
    isLoadingSearchedUsers: USE_MOCK_DATA ? false : searchUsersQuery.isLoading,
    isRefetchingSearchedUsers: USE_MOCK_DATA
      ? false
      : searchUsersQuery.isRefetching,

    // Guest contacts
    guestContacts: guestContactsQuery.data?.rate_limited_numbers || [],

    // Combined loading states
    isLoadingUsersContacts: USE_MOCK_DATA ? false : searchUsersQuery.isLoading,
    isRefreshingUsersContacts: USE_MOCK_DATA
      ? false
      : searchUsersQuery.isRefetching,

    // Refresh function
    onRefreshUsersContacts: () => {
      if (!USE_MOCK_DATA) {
        searchUsersQuery.refetch()
        guestContactsQuery.refetch()
      }
    },

    // Device contacts (not available in mini app)
    deviceContactsWithoutUserContacts: [],
    userContacts: { users: [] },
    hasPermissionToAccessContacts: false,
  }
}
